import React, { use } from 'react';

import { MembersLoginButton } from 'src/components/common/members-login-button';
import { createMobileNavStyle } from 'src/components/layout/mobile-nav/mobile-nav-helper';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import { getClubDisplayValues } from 'src/enums/build/build-type';
import { clubNameDependingOnBuildTypeAndLanguage } from 'src/helpers/club-helper';
import { shouldApplyMembersLoginButton } from 'src/helpers/members-login-helper';
import type { NavigationPage } from 'src/models/cms/navigation-page';
import { TranslatedStaticText } from 'src/translators/static-text-translator';

import { ClubCmsContent } from '../../../content/club-content-consumer';
import { colors } from '../../../styling/constants';
import mq from '../../../styling/media-queries';
import LanguagePicker from '../../common/language-picker';

import MobileNavClub from './mobile-nav-club';
import MobileNavLink from './mobile-nav-link';

type MobileNavProps = {
  clubName?: string;
  closeNav: () => void;
  mobileNavigationOpened: boolean;
  bannerIsActive: boolean;
};

const MobileNav: React.FC<MobileNavProps> = ({
  clubName,
  closeNav,
  mobileNavigationOpened,
  bannerIsActive,
}) => {
  const clubCmsData = use(ClubCmsContent);
  const globalCmsData = use(GlobalCmsContent);
  const { styling, buildType, isGlobalPage, language } = use(SiteContext);
  const componentStyles = createMobileNavStyle(styling, buildType, bannerIsActive);

  const navigationPagesToDisplay = isGlobalPage
    ? globalCmsData.sharedContent?.navigationPagesToDisplay
    : clubCmsData.navigationMenuContent?.navigationPagesToDisplay;

  const applyMembersLoginButton = shouldApplyMembersLoginButton(isGlobalPage, buildType);

  return (
    <div css={mq({ display: [mobileNavigationOpened ? 'flex' : 'none', 'none'] })}>
      <div css={componentStyles.wrapperStyling}>
        <MobileNavClub clubName={clubName} styling={styling} onFinish={closeNav} />
        <div css={componentStyles.staticTextWrapperStyling}>
          <div css={[styling.fonts.regular, { color: colors.beige }]}>
            {isGlobalPage ? (
              <TranslatedStaticText
                lookupKey="atUnit"
                params={{ unitName: getClubDisplayValues(buildType).displayName }}
              />
            ) : (
              <TranslatedStaticText
                lookupKey="atClub"
                params={{
                  clubName: clubNameDependingOnBuildTypeAndLanguage(clubName, buildType, language),
                }}
              />
            )}
            :
          </div>
          <div css={componentStyles.linksWrapperStyling}>
            {navigationPagesToDisplay?.mainPages.map((navigationPage: NavigationPage) => (
              <MobileNavLink
                key={navigationPage.link}
                id={navigationPage.link}
                path={navigationPage.link}
                text={navigationPage.displayText}
                closeNav={closeNav}
              />
            ))}
          </div>
        </div>
        <LanguagePicker />
        <div css={componentStyles.navigationPageStyling}>
          {navigationPagesToDisplay?.secondaryPages &&
            navigationPagesToDisplay?.secondaryPages?.length > 0 && (
              <div css={componentStyles.secondaryLinksWrapperStyling}>
                {navigationPagesToDisplay?.secondaryPages?.map((navigationPage: NavigationPage) => (
                  <MobileNavLink
                    key={navigationPage.link}
                    id={navigationPage.link}
                    path={navigationPage.link}
                    text={navigationPage.displayText}
                    closeNav={closeNav}
                  />
                ))}
              </div>
            )}
          {applyMembersLoginButton && <MembersLoginButton />}
        </div>
      </div>
    </div>
  );
};

export default MobileNav;
